import { Encoder, Decoder } from '@msgpack/msgpack';
import * as encoding from 'lib0/encoding';

import { type Message } from '@canvas/protocol-types';

import {
  decodeMessageKeys,
  encodeMessageKeys,
  type EncodedMessage,
} from '../message-key.ts';

const encoder = new Encoder();
const decoder = new Decoder();

export function encodeMessageV2(message: Message): Uint8Array<ArrayBuffer> {
  const keyEncodedMessage = encodeMessageKeys(message);

  return encoder.encode(keyEncodedMessage);
}

export function decodeMessageV2(encodedMessage: Uint8Array<ArrayBuffer>): Message {
  const keyEncodedMessage = decoder.decode(encodedMessage) as EncodedMessage;
  return decodeMessageKeys(keyEncodedMessage);
}

const LEGACY_AGENT_CLIENT_ID = 0xa1;
const RESERVED_AGENT_CLIENT_ID_FALLBACK = 0xa2;

/**
 * Matches NCS's stable external-agent participant ID algorithm:
 * https://bitbucket.org/atlassian/pf-collab-service/src/5cce8dc2e1266eee1fa645916c055560265bdda6/src/koa/controllers/agents/canvas-awareness.ts#lines-76:91
 */
export function getDeterministicAwarenessClientId(participantKey: string): number {
  let hash = 0;

  for (const character of participantKey) {
    hash = (hash * 31 + character.charCodeAt(0)) & 0x7fffffff;
  }

  return hash === 0 || hash === LEGACY_AGENT_CLIENT_ID
    ? RESERVED_AGENT_CLIENT_ID_FALLBACK
    : hash;
}

/**
 * Matches the string-encoded Yjs awareness payload used by NCS:
 * https://bitbucket.org/atlassian/pf-collab-service/src/5cce8dc2e1266eee1fa645916c055560265bdda6/src/koa/controllers/agents/canvas-awareness.ts#lines-154:179
 */
export function encodeStringAwarenessUpdate({
  clientId,
  clock,
  state,
}: {
  clientId: number;
  clock: number;
  state: Readonly<Record<string, unknown>> | null;
}): Uint8Array {
  const updateEncoder = encoding.createEncoder();
  encoding.writeVarUint(updateEncoder, 1);
  encoding.writeVarUint(updateEncoder, clientId);
  encoding.writeVarUint(updateEncoder, clock);
  encoding.writeVarString(updateEncoder, JSON.stringify(state));

  const participantEncoder = encoding.createEncoder();
  encoding.writeVarUint8Array(participantEncoder, encoding.toUint8Array(updateEncoder));
  return encoding.toUint8Array(participantEncoder);
}
